import { computed } from 'vue';
import { useStore } from 'vuex';
import { ModalId, ModalLocalStorageKey } from '@/types/modal';
import useTranslate from '@/utilities/composables/useTranslate';
import useCurrentRole from '@/utilities/composables/useCurrentRole';
import { useRouter } from 'vue-router';
import { browserStorageGetItem, browserStorageSetItem } from '@/utilities/browser-storage.util';
import planData, { PlanId } from '@/components/sections/settings/planData';
import { TrialStatus } from '@superthread-com/api';

export default function useTrialPlan() {
  const store = useStore();
  const { translate } = useTranslate();
  const { isElevated } = useCurrentRole();
  const router = useRouter();

  const daysLeftOnTrial = computed(() => {
    if (!isOnTrial.value) return 0;

    const now = Date.now();
    const trialEndDate = teamSub.value.trial.end * 1000;
    const msPerDay = 1000 * 60 * 60 * 24;
    const diffMs = trialEndDate - now;

    if (diffMs <= 0) {
      return 0; // Trial has expired
    }

    return Math.ceil(diffMs / msPerDay);
  });

  const teamSub = computed(() => {
    return store.getters.getTeamSubscription || {};
  });

  const isOnTrial = computed(() => {
    return (teamSub.value.status === 'trialing' && teamSub.value.trial) || false;
  });

  const freePlanTitle = computed((): string => {
    return planData[PlanId.Free].title;
  });

  const currentPlanName = computed(() => {
    return (
      (store.getters.getAvailablePlans || []).find((plan: any) => {
        return plan.id === store.getters.getTeamSubscription?.plan_id;
      })?.name || freePlanTitle.value
    );
  });

  const expirationModalLsKey = computed((): string => {
    return `${ModalLocalStorageKey.TrialExpirationShown}-${store.getters.getTeamID}`;
  });

  const setExpirationModalShownLs = (status: string): void => {
    const currentLs = getExpirationModalShownLs() || {};
    browserStorageSetItem(expirationModalLsKey.value, {
      ...currentLs,
      [status]: true,
    });
  };

  const getExpirationModalShownLs = (): { [key: string]: boolean } => {
    return browserStorageGetItem(expirationModalLsKey.value as any) || {};
  };

  const showTrialExpirationModal = (trialStatus: TrialStatus) => {
    if (trialStatus === TrialStatus.Active) {
      return;
    }

    const isNearExpiry = trialStatus === TrialStatus.NearExpiry;

    const description = isNearExpiry
      ? [
          translate('billing.thereAreDaysLeftOnYourTrial_one', {
            count: daysLeftOnTrial.value,
            planName: currentPlanName.value,
          }),
          isElevated.value
            ? translate('billing.afterTrialLong', { planName: freePlanTitle.value })
            : translate('billing.afterTrialShort', { planName: freePlanTitle.value }),
        ]
      : [
          isElevated.value
            ? translate('billing.youHaveBeenDowngradedLong', { planName: freePlanTitle.value })
            : translate('billing.youHaveBeenDowngradedShort', { planName: freePlanTitle.value }),
        ];

    const title = isNearExpiry
      ? translate('billing.yourTrialEndsSoon')
      : translate('billing.yourTrialHasExpired');

    const confirmButtonText = isElevated.value
      ? translate('billing.seeAvailablePlans')
      : translate('billing.contactAdminOrOwner');

    const onConfirmFn = () => {
      router.push({ name: isElevated.value ? 'billing' : 'members' });
    };

    store.dispatch('setModal', {
      id: ModalId.TrialExpiration,
      meta: {
        title,
        description,
        showCancelButton: false,
        confirmButtonText,
        onConfirmFn,
      },
    });
  };

  return {
    isOnTrial,
    daysLeftOnTrial,
    currentPlanName,
    showTrialExpirationModal,
    expirationModalLsKey,
    setExpirationModalShownLs,
    getExpirationModalShownLs,
  };
}
